import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { CardUIData } from '../../types/cardDataTypes';
import { ComparisonSection } from './ComparisonSection';
import { FONT_CONFIG, TREND_COLORS } from './constants';
import { CardTileHeading } from '../CardTileHeading';

interface ReportMetricCardProps {
  data: CardUIData;
  selectConfig?: {
    isChecked: boolean;
    onChange: (_checked: boolean) => void;
  };
  containerProps?: Record<string, unknown>;
  isSelect?: boolean;
  isFullScreen?: boolean;
  onCardClick?: (_e: React.MouseEvent) => void;
  tooltipProps?: Record<string, unknown>;
  onDrillDownOpen?: (_data: Record<string, unknown>) => void;
  isStaticReport?: boolean;
  onTileMetaChange?: (
    _tileId: string,
    _metadata: Record<string, unknown>
  ) => void;
  tileData?: {
    id?: string;
    metadata?: { title?: string };
    subject_type?: string;
  };
  currentTitle?: string;
  isPrintView?: boolean;
  /** Display labels for applied filters not supported by this block (e.g. ["Time", "Survey name"]) */
  unsupportedFilterLabels?: string[];
}

export const ReportMetricCard: React.FC<ReportMetricCardProps> = ({
  tileData,
  data,
  selectConfig,
  containerProps,
  isSelect = false,
  onCardClick = undefined,
  isFullScreen = false,
  tooltipProps = {},
  onDrillDownOpen = (_data: Record<string, unknown>) => {},
  isStaticReport = false,
  onTileMetaChange,
  currentTitle,
  isPrintView = false,
  unsupportedFilterLabels,
}) => {
  const {
    topLabel,
    bottomLabel,
    metricDefinition,
    basedOn,
    category,
    value,
    percentageDiff,
    trend,
    comparisonLabel,
  } = data || {};

  const { truncationLength } = useContext(ReportSelectionContext);
  const [flexDirection, setFlexDirection] = useState<'row' | 'column'>('row');
  const [fontSizes, setFontSizes] = useState({
    main: `${FONT_CONFIG.main.base}px`,
    comparison: `${FONT_CONFIG.comparison.base}px`,
    bottomLabel: `${FONT_CONFIG.bottomLabel.base}px`,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const trendColor = trend ? TREND_COLORS[trend] : undefined;

  useEffect(() => {
    const checkSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = containerRef.current.offsetHeight;

        setFlexDirection(width < 200 ? 'column' : 'row');

        const baseHeight = 100;
        const heightRatio = height / baseHeight;

        const calculateFontSize = (
          config: (typeof FONT_CONFIG)[keyof typeof FONT_CONFIG]
        ) => {
          // Reduce font sizes for print view
          const printScale = isPrintView ? 0.7 : 1;
          const scale = Math.max(
            config.min,
            Math.min(config.max, heightRatio * config.scale * printScale)
          );
          return `${Math.floor(config.base * scale)}px`;
        };

        setFontSizes({
          main: calculateFontSize(FONT_CONFIG.main),
          comparison: calculateFontSize(FONT_CONFIG.comparison),
          bottomLabel: calculateFontSize(FONT_CONFIG.bottomLabel),
        });
      }
    };

    const resizeObserver = new ResizeObserver(checkSize);
    //don't resize in add block container
    if (containerRef.current && !isSelect) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [isSelect, isPrintView]);

  return (
    <Tooltip {...tooltipProps}>
      <Flex
        ref={containerRef}
        bg="white"
        height="100%"
        flexDirection="column"
        onClick={(e) => {
          onCardClick?.(e);
        }}
        cursor={onCardClick ? 'pointer' : undefined}
        borderRadius="sm"
        border={isSelect && '2px solid'}
        p={isSelect && '12px 20px 12px 20px'}
        borderColor={selectConfig?.isChecked ? 'ats-primary' : 'border-color'}
        justifyContent="space-between"
        {...containerProps}
      >
        <CardTileHeading
          tileId={tileData?.id}
          checkboxId={tileData?.id}
          label={tileData?.metadata?.title ?? topLabel}
          metricDefinition={isFullScreen ? undefined : metricDefinition}
          basedOn={basedOn}
          category={category}
          selectConfig={selectConfig}
          truncationLength={truncationLength || 0}
          isStaticReport={isStaticReport}
          onTileMetaChange={onTileMetaChange}
          currentTitle={currentTitle}
          subjectType={tileData?.subject_type}
          unsupportedFilterLabels={unsupportedFilterLabels}
          isPrintView={isPrintView}
        />
        <Box>
          <Flex
            flex="1"
            gap="8px"
            direction={flexDirection}
            align={flexDirection === 'column' ? 'flex-start' : 'center'}
          >
            {(() => {
              return (
                <Text
                  fontSize={fontSizes.main}
                  fontWeight="semiBold"
                  flexShrink={0}
                  color="primary-900"
                  cursor="pointer"
                  _hover={{
                    textDecoration: 'underline',
                  }}
                  userSelect="none"
                  onClick={() => {
                    const cardData = {
                      metric: topLabel,
                      type: 'metrics',
                      metricValue: value,
                    };
                    onDrillDownOpen(cardData);
                  }}
                >
                  {value}
                </Text>
              );
            })()}

            {tileData?.subject_type !== 'live_job_pipeline' && (
              <Box flex="1" minW="0">
                <ComparisonSection
                  trend={trend}
                  trendColor={trendColor}
                  percentageDiff={percentageDiff}
                  comparisonLabel={comparisonLabel}
                  fontSize={fontSizes.comparison}
                />
              </Box>
            )}
          </Flex>
          <Box>
            <Text
              size="sm-medium"
              fontSize={isPrintView ? '9px' : '12px'}
              fontWeight={isPrintView ? '550' : '650'}
              pb="3px"
              userSelect="none"
            >
              {bottomLabel}
            </Text>
          </Box>
        </Box>
      </Flex>
    </Tooltip>
  );
};
